import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import * as Sentry from "@sentry/browser";
import axios, { endpoints } from 'src/utils/axios';
//
import { AuthContext } from './auth-context';
import { setSession } from './utils';
import {ActionMapType, AuthStateType, AuthUserType, ConfigType} from '../../types';
import creds from "../../../api/creds";

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
  RELOAD = 'RELOAD',
  RELOAD_NOTIF = 'RELOAD_NOTIF',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
    config: ConfigType,
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.RELOAD]: {
    user: AuthUserType
    config: ConfigType,
  };
  [Types.RELOAD_NOTIF]: {
    notificationsData: any,
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  config: null,
  loading: true,
  notificationsData: {list: []},
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    Sentry.setUser({ id: action.payload.user?.id, email: action.payload.user?.email, username: action.payload.user?.username, ver: process.env.REACT_APP_CLIENT_VERSION });
    return {
      loading: false,
      user: action.payload.user,
      config: action.payload.config,
      notificationsData: action.payload.user?.notifications,
    };
  }
  if (action.type === Types.LOGIN) {
    Sentry.setUser({ id: action.payload.user?.id, email: action.payload.user?.email, username: action.payload.user?.username, ver: process.env.REACT_APP_CLIENT_VERSION });
    return {
      ...state,
      user: action.payload.user,
      notificationsData: action.payload.user?.notifications,
    };
  }
  if (action.type === Types.REGISTER) {
    Sentry.setUser({ id: action.payload.user?.id, email: action.payload.user?.email, username: action.payload.user?.username, ver: process.env.REACT_APP_CLIENT_VERSION });
    return {
      ...state,
      user: action.payload.user,
      notificationsData: action.payload.user?.notifications,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  if (action.type === Types.RELOAD) {
    Sentry.setUser({ id: action.payload.user?.id, email: action.payload.user?.email, username: action.payload.user?.username, ver: process.env.REACT_APP_CLIENT_VERSION });
    return {
      ...state,
      user: action.payload.user,
      config: action.payload.config,
      notificationsData: action.payload.user?.notifications,
    };
  }
  if (action.type === Types.RELOAD_NOTIF) {
    return {
      ...state,
      notificationsData: action.payload.notificationsData,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    let suffix = '';
    if(creds.data.getPushNotifToken()) {
      suffix = `?push_token=${encodeURIComponent(creds.data.getPushNotifToken())}`;
    }
    try {
      const res = await axios.get(endpoints.auth.me + suffix, {
        headers: {
          'x-session-id': await creds.data.getCookie(),
          'x-client-version': process.env.REACT_APP_MOBILE ? process.env.REACT_APP_CLIENT_VERSION : undefined,
        }
      });

      const { user, config } = res.data;
      await creds.setCookie(user.sessionId);

      dispatch({
        type: Types.INITIAL,
        payload: {
          user,
          config,
        },
      });

    } catch (error) {
      if(error.response?.status === 426) {
        window.location.href = '/update';
        return;
      }
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
          config: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string, groupSchoolId?: string, groupSchoolYear?: number) => {
    let suffix = '';
    if(creds.data.getPushNotifToken()) {
      suffix = `?push_token=${encodeURIComponent(creds.data.getPushNotifToken())}`;
    }
    const data = {
      email,
      password,
      groupSchoolId,
      groupSchoolYear,
    };

    const res = await axios.post(endpoints.auth.login + suffix, data, {
      validateStatus: (status) => [200, 401, 426].indexOf(status) !== -1,
      headers: {
        'x-session-id': await creds.data.getCookie(),
      }
    });

    if(res.status === 401) {
      throw new Error('Грешно потребителско име или парола!');
    }

    const { accessToken, user } = res.data;

    await creds.setCookie(user.sessionId);

    setSession(accessToken);

    dispatch({
      type: Types.LOGIN,
      payload: {
        user,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(
    async (email: string,
           username: string,
           password: string,
           grade: number,
           city: string,
           school: string) => {
      let suffix = '';
      if(creds.data.getPushNotifToken()) {
        suffix = `?push_token=${encodeURIComponent(creds.data.getPushNotifToken())}`;
      }
      const data = {
        email,
        username,
        password,
        grade,
        city,
        school,
      };

      const res = await axios.post(endpoints.auth.register + suffix, data, {
        headers: {
          'x-session-id': await creds.data.getCookie(),
        }
      });

      const { accessToken, user } = res.data;

      sessionStorage.setItem(STORAGE_KEY, accessToken);
      await creds.setCookie(user.sessionId);

      dispatch({
        type: Types.REGISTER,
        payload: {
          user,
        },
      });
    },
    []
  );

  // REGISTER GUEST
  const registerGuest = useCallback(
    async (gender: string) => {
      let suffix = '';
      if(creds.data.getPushNotifToken()) {
        suffix = `?push_token=${encodeURIComponent(creds.data.getPushNotifToken())}`;
      }

      const data = {
        gender,
      };

      const res = await axios.post(endpoints.auth.registerGuest + suffix, data, {
        headers: {
          'x-session-id': await creds.data.getCookie(),
        }
      });

      const { accessToken, user } = res.data;

      sessionStorage.setItem(STORAGE_KEY, accessToken);
      await creds.setCookie(user.sessionId);

      dispatch({
        type: Types.REGISTER,
        payload: {
          user,
        },
      });
    },
    []
  );

  // LOGOUT
  const logout = useCallback(async () => {
    await axios.get(endpoints.auth.logout, {
      headers: {
        'x-session-id': await creds.data.getCookie(),
      }
    });
    await creds.resetCookie();
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  // RELOAD
  const reload = useCallback(async () => {
    let suffix = '';
    if(creds.data.getPushNotifToken()) {
      suffix = `?push_token=${encodeURIComponent(creds.data.getPushNotifToken())}`;
    }
    const res = await axios.get(endpoints.auth.me + suffix, {
      headers: {
        'x-session-id': await creds.data.getCookie(),
      }
    });

    const { user, config } = res.data;
    await creds.setCookie(user.sessionId);

    dispatch({
      type: Types.RELOAD,
      payload: {
        user,
        config,
      },
    });

    return {user, config};
  }, []);

  const updateStateNVO = useCallback((stateNVO: any) => {
    dispatch({
      type: Types.RELOAD,
      payload: {
        user: {
          ...state.user,
          state: {
            ...(state.user?.state || {}),
            nvo: stateNVO ?? undefined,
          },
        },
        config: state.config,
      },
    });
  }, [state.config, state.user]);

  // RELOAD NOTIF
  const reloadNotifications = useCallback(async () => {
    let suffix = '';
    if(creds.data.getPushNotifToken()) {
      suffix = `&push_token=${encodeURIComponent(creds.data.getPushNotifToken())}`;
    }
    if(!state.user) return;
    const res = await axios.get(`${endpoints.auth.me}?notifications_only=true${suffix}`, {
      headers: {
        'x-session-id': await creds.data.getCookie(),
      }
    });

    const { user } = res.data;

    dispatch({
      type: Types.RELOAD_NOTIF,
      payload: {
        notificationsData: user.notifications,
      },
    });
  }, [state.user]);

  // RELOAD NVO
  const reloadNvo = useCallback(async () => {
    if(!state.user) return;
    const res = await axios.get(`${endpoints.auth.me}?nvo_only=true`, {
      headers: {
        'x-session-id': await creds.data.getCookie(),
      }
    });

    const { nvo } = res.data;

    updateStateNVO(nvo);
  }, [state.user, updateStateNVO]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      config: state.config,
      notificationsData: state.notificationsData,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
      reload,
      registerGuest,
      reloadNotifications,
      reloadNvo,
      updateStateNVO,
    }),
    [login, logout, register, reload, state.user, state.config, state.notificationsData, status, registerGuest, reloadNotifications, reloadNvo, updateStateNVO]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
